import type { TextFieldProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  ControllerProps,
  FieldError,
  UseFormReturn,
  useWatch
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormItem } from 'components/shared/FormItem';
import { FormTitle } from 'components/shared/FormTitle';

import { TextFieldElement } from './TextFieldElement';

import './index.css';

const CustomTextField = styled(TextFieldElement)(() => ({
  '.MuiFormHelperText-root': {
    position: 'absolute',
    top: '-22px',
    right: '0px'
  }
}));

const HelperText = styled(Typography)(() => ({
  fontFamily: 'Roboto',
  fontWeight: 300,
  color: '#6B6363',
  fontSize: 10,
  paddingBottom: 0
}));

export type TextFieldElementProps = Omit<TextFieldProps, 'name'> & {
  validation?: ControllerProps['rules'];
  name: string;
  parseError?: (error: FieldError) => string;
  form: UseFormReturn<any, any>;
  title?: string;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  hasHelperText?: boolean;
  customValue?: string;
};

function TextAreaElement({
  validation = {},
  parseError,
  type,
  required = false,
  name,
  form,
  title,
  disabled = false,
  maxLength = 5000,
  hasHelperText = true,
  rows = 4,
  maxRows = null,
  ...rest
}: TextFieldElementProps) {
  const { control } = form;
  const { t } = useTranslation('global');

  const textAreaValue = useWatch({ control, name }) as string;

  const length = rest?.customValue
    ? rest.customValue?.length
    : textAreaValue?.length;

  return (
    <>
      {title && (
        <>
          <FormTitle display="inline" variant="body2" gutterBottom>
            {title || '* Descripción'}
          </FormTitle>
          <HelperText display="inline" marginLeft={1}>
            {`${t('common.commonProperties.upTo')} ${maxLength} ${t(
              'common.commonProperties.characters'
            )}`}
          </HelperText>
        </>
      )}
      <FormItem className="TextAreaElementResponsive">
        <CustomTextField
          {...rest}
          control={control}
          disabled={disabled}
          helperText={hasHelperText ? `${length || 0}/${maxLength}` : ''}
          inputProps={{ maxLength }}
          maxRows={maxRows}
          name={name}
          required={required}
          rows={maxRows ? 0 : rows}
          validation={{ required, maxLength, ...validation }}
          fullWidth
          multiline
        />
      </FormItem>
    </>
  );
}

export default TextAreaElement;
